import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { enqueueSnackbar } from '~/utils/notifications';
import { ReadonlyVec3 } from 'gl-matrix';
import { Modality } from '~/domain/dicom/modality';
import { formatPetDisplayValue } from '~/domain/dicom/unitSuffix';
import { Image3D } from '~/domain/patient/image3d';
import {
    environmentState,
    lowerSideViewState,
    mainViewState,
    upperSideViewState,
} from '~/state/atoms/engineAtoms';
import {
    primaryImageState,
    secondaryImageState,
} from '~/state/atoms/image/imageAtoms';
import { petMaxSuvState } from '~/state/atoms/image/petImageAtom';
import { getMissingSUVParams } from '~/domain/patient/petSuv';

const isPet = (image: Image3D | undefined): boolean =>
    image?.firstSlice.modality === Modality.PT;

/**
 * Centralised PET-volume selector. Reports which side of the fusion
 * holds PET data, exposes the SUV-aware formatting / navigation
 * helpers, and synchronises the global SUV-max readout. PET may be
 * loaded as primary, secondary, or both — secondary wins when both,
 * matching the "secondary = colour overlay" convention used by the
 * renderer.
 */
export default function usePetImage() {
    const environment = useRecoilValue(environmentState);
    const mainView = useRecoilValue(mainViewState);
    const upperSideView = useRecoilValue(upperSideViewState);
    const lowerSideView = useRecoilValue(lowerSideViewState);
    const primaryImage = useRecoilValue(primaryImageState);
    const secondaryImage = useRecoilValue(secondaryImageState);

    const isPetSecondary = isPet(secondaryImage);
    const isPetPrimary = isPet(primaryImage);
    const petImage: Image3D | undefined = isPetSecondary
        ? secondaryImage
        : isPetPrimary
          ? primaryImage
          : undefined;

    const suvFactor = petImage?.suvFactor ?? null;
    const maxPixelValue = petImage?.maxValue ?? 1;

    usePetMaxSuvSync(petImage, suvFactor, maxPixelValue);

    const maxIntensityPosition = useMemo<ReadonlyVec3 | null>(
        () => petImage?.findMaxIntensityPosition() ?? null,
        [petImage]
    );

    const formatNormalizedPetValue = useCallback(
        (normalized: number): string =>
            formatPetDisplayValue(normalized * maxPixelValue, suvFactor),
        [maxPixelValue, suvFactor]
    );

    const navigateToMaxIntensity = useCallback(() => {
        if (
            !environment ||
            !mainView ||
            !upperSideView ||
            !lowerSideView ||
            !maxIntensityPosition
        ) {
            return;
        }
        environment.panZoom.focusToPoint(
            mainView,
            upperSideView,
            lowerSideView,
            maxIntensityPosition
        );
    }, [
        environment,
        mainView,
        upperSideView,
        lowerSideView,
        maxIntensityPosition,
    ]);

    return {
        isPetSecondary,
        isPetPrimary,
        petImage,
        maxPixelValue,
        // hasSuv distinguishes SUV-calibrated PET from raw-uptake PET so
        // UI copy can use clinical terminology ("SUVmax" vs "maximum
        // uptake") rather than the ambiguous "max intensity".
        hasSuv: suvFactor !== null,
        maxIntensityLabel: formatPetDisplayValue(maxPixelValue, suvFactor),
        maxIntensityPosition,
        formatNormalizedPetValue,
        navigateToMaxIntensity,
    };
}

/**
 * Mirrors the active PET volume's SUV max into Recoil, and warns
 * the operator once when SUV conversion is unavailable so the raw
 * Bq/ml fallback is never silent.
 *
 * Extracted from the main hook so the data-selection path stays
 * declarative; the side-effect's `processedImageRef` guard prevents
 * the warning toast from re-firing on every Recoil tick.
 */
function usePetMaxSuvSync(
    petImage: Image3D | undefined,
    suvFactor: number | null,
    maxPixelValue: number
): void {
    const setPetMaxSuv = useSetRecoilState(petMaxSuvState);
    const processedImageRef = useRef<Image3D | null>(null);

    useEffect(() => {
        if (!petImage) {
            setPetMaxSuv(null);
            processedImageRef.current = null;
            return;
        }
        if (processedImageRef.current === petImage) {
            return;
        }
        processedImageRef.current = petImage;

        if (suvFactor !== null) {
            const maxSuv = maxPixelValue * suvFactor;
            setPetMaxSuv(Math.round(maxSuv * 100) / 100);
            return;
        }

        setPetMaxSuv(null);
        const petMetadata = petImage.firstSlice.petMetadata;
        const missingParams = petMetadata
            ? getMissingSUVParams(petMetadata)
            : ['PET metadata'];
        enqueueSnackbar(
            `SUVbw values cannot be computed. Missing DICOM parameters: ${missingParams.join(', ')}. Displaying raw intensity (Bq/ml).`,
            {
                variant: 'warning',
                preventDuplicate: true,
            }
        );
    }, [petImage, suvFactor, maxPixelValue, setPetMaxSuv]);
}
